import { Alert, Button, Dropdown, Empty, Input, Modal, Spin, Tag, message } from 'antd';
import {
    CopyOutlined,
    DownloadOutlined,
    MoreOutlined,
    ReloadOutlined,
    ShareAltOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { get, post } from '../../axios/axios';
import { DownloadPaperModal } from '../../Article/Documents/Step3/components/DownloadPaperModal';
import {
    buildPackageShareUrl,
    getPackageDestination,
    getRecentPackageOrders,
    rememberPackageOrder,
    selectPackageEntitlement,
} from '../../Service/packageEntitlements';
import styles from './GroupBuyingOrder.module.scss';

async function copyText(value) {
    if (!value) throw new Error('没有可复制的内容');
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(value);
            return;
        }
    } catch (_) {
        /* 部分浏览器需使用兼容复制方式。 */
    }
    const input = document.createElement('textarea');
    input.value = value;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    try {
        input.select();
        if (!document.execCommand('copy')) throw new Error('复制失败，请手动复制');
    } finally {
        input.remove();
    }
}

const needsServiceAssistance = (record) =>
    /^MLM(T|S)/.test(record.redeemCode || '') || /^(任务书|调查问卷)/.test(record.redeemName || '');

/**
 * 套餐查询内容（不含弹窗外壳）：既用于「我的套餐」弹窗，也可直接嵌在别处（如顶部「订单查询」的标签页）。
 * visible 变为 true 时刷新最近查询并自动查上一次的凭证；onClose 在跳转使用 / 查看结果前调用。
 */
export const GroupBuyingOrderPanel = ({
    initialOrderNo = '',
    onSelect,
    paymentCompleted = false,
    visible = true,
    onClose,
}) => {
    const [orderNo, setOrderNo] = useState(initialOrderNo);
    const [queriedOrderNo, setQueriedOrderNo] = useState('');
    const [records, setRecords] = useState(null);
    const [recentOrders, setRecentOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sharing, setSharing] = useState('');
    const [resuming, setResuming] = useState('');
    const [shareLink, setShareLink] = useState('');
    const requestId = useRef(0);
    const lastQueriedOrder = useRef('');
    const retryTimer = useRef(null);
    const cancelQuery = useCallback(() => {
        requestId.current += 1;
        clearTimeout(retryTimer.current);
    }, []);

    const queryOrder = useCallback(async (value, retries = 0) => {
        const credential = String(value || '').trim();
        if (!credential) {
            message.warning('请输入购买套餐时的订单号或支付交易单号');
            return;
        }
        clearTimeout(retryTimer.current);
        const currentRequest = ++requestId.current;
        lastQueriedOrder.current = credential;
        setOrderNo(credential);
        setQueriedOrderNo(credential);
        setLoading(true);
        setError('');
        setRecords(null);
        setShareLink('');
        try {
            const res = await get('/member/order/list', { orderNo: credential }, { slient: true });
            if (!res.success || !Array.isArray(res.data))
                throw new Error(res.msg || '未查到套餐，请检查购买订单号或支付交易单号');
            if (currentRequest !== requestId.current) return;
            setRecords(res.data);
            if (res.data.length) {
                rememberPackageOrder(credential);
                setRecentOrders(getRecentPackageOrders());
            } else if (retries > 0) {
                retryTimer.current = setTimeout(() => queryOrder(credential, retries - 1), 2000);
            }
        } catch (err) {
            if (currentRequest === requestId.current)
                setError(err.response?.data?.msg || err.message || '查询失败，请稍后重试');
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (visible) {
            setLoading(false);
            setRecentOrders(getRecentPackageOrders());
            setShareLink('');
            const credential = initialOrderNo || lastQueriedOrder.current;
            if (credential) queryOrder(credential, initialOrderNo && paymentCompleted ? 4 : 0);
        }
        return () => {
            cancelQuery();
        };
    }, [visible, initialOrderNo, paymentCompleted, queryOrder, cancelQuery]);

    const handleUse = async (record) => {
        if (needsServiceAssistance(record)) {
            Modal.info({
                zIndex: 1920,
                title: `${record.redeemName || '这项服务'}的使用方式`,
                content:
                    '这项服务暂未开放独立创作入口。请保留这份套餐，通过页面右下角客服入口联系协助使用；本次查看不会消耗套餐。',
                okText: '知道了',
            });
            return;
        }
        try {
            if (onSelect) {
                await onSelect(record);
                onClose?.();
                return;
            }
            const destination = getPackageDestination(record);
            selectPackageEntitlement(record);
            onClose?.();
            window.location.assign(destination);
        } catch (err) {
            message.error(err.message || '暂时无法使用，请重试');
        }
    };

    const handleShare = async (record) => {
        setSharing(record.redeemCode);
        try {
            const link = await buildPackageShareUrl(record);
            setShareLink(link);
            await copyText(link);
            message.success('已复制本份套餐的领取链接，可发送给需要使用的人');
        } catch (err) {
            message.error(err.message || '分享失败，请稍后重试');
        } finally {
            setSharing('');
        }
    };

    const handleCopyCode = async (record) => {
        try {
            await copyText(record.redeemCode);
            message.success('兑换码已复制');
        } catch (err) {
            message.error(err.message || '复制失败');
        }
    };

    const handleViewResult = (record) => {
        onClose?.();
        NiceModal.show(DownloadPaperModal, {
            orderNo: record.usedOrderNo || record.redeemCode,
            fallbackOrderNo: record.redeemCode,
            queryType: 'query',
            queryPath: '/ai/paper/query',
        });
    };

    const handleResume = async (record) => {
        if (resuming) return;
        setResuming(record.redeemCode);
        try {
            const res = await post('/member/payment/redeemCodeNew', {
                orderNo: record.usedOrderNo,
                code: record.redeemCode,
            });
            if (!res.success || res.data === false)
                throw new Error(res.msg || '暂时无法继续生成，请重试');
            message.success('已继续生成，无需再次购买');
            await queryOrder(queriedOrderNo);
            handleViewResult(record);
        } catch (err) {
            message.error(err.response?.data?.msg || err.message || '继续生成失败，请重试');
        } finally {
            setResuming('');
        }
    };

    const packages = (records || []).filter((item) => Number(item.codeType) !== 1);
    const coupons = (records || []).filter((item) => Number(item.codeType) === 1);
    const availableCount = packages.filter((item) => Number(item.codeUse) !== 1).length;
    return (
        <div className={styles.orderModal}>
            <p className={styles.intro}>
                未使用的套餐可直接开始创作；已使用的套餐可查看生成进度并下载文件。
            </p>
            <div className={styles.search}>
                <Input
                    aria-label="套餐订单号或支付交易单号"
                    value={orderNo}
                    placeholder="套餐订单号 / 微信或支付宝交易单号"
                    onChange={(event) => setOrderNo(event.target.value)}
                    onPressEnter={() => queryOrder(orderNo)}
                    allowClear
                />
                <Button type="primary" loading={loading} onClick={() => queryOrder(orderNo)}>
                    查找套餐
                </Button>
            </div>
            {recentOrders.length > 0 && (
                <div className={styles.recent}>
                    <span>本机最近查询：</span>
                    {recentOrders.slice(0, 4).map((item) => (
                        <Button
                            key={item.orderNo}
                            size="small"
                            onClick={() => queryOrder(item.orderNo)}
                        >
                            订单尾号 {item.orderNo.slice(-6)}
                        </Button>
                    ))}
                    <p>记录仅保存在当前浏览器。换设备后可凭购买订单号或支付交易单号找回。</p>
                </div>
            )}
            {error && (
                <Alert
                    showIcon
                    type="warning"
                    message={error}
                    action={
                        <Button size="small" onClick={() => queryOrder(queriedOrderNo)}>
                            重试
                        </Button>
                    }
                />
            )}
            <Spin spinning={loading}>
                {records?.length > 0 ? (
                    <>
                        <div className={styles.summary}>
                            <span>
                                可使用 <strong>{availableCount}</strong> 份 · 已使用{' '}
                                {packages.length - availableCount} 份
                            </span>
                            <Button
                                type="text"
                                icon={<ReloadOutlined />}
                                onClick={() => queryOrder(queriedOrderNo)}
                            >
                                刷新状态
                            </Button>
                        </div>
                        <div className={styles.cards}>
                            {packages.map((record, index) => {
                                const used = Number(record.codeUse) === 1;
                                const canResume =
                                    used &&
                                    record.usageStatus != null &&
                                    [0, 2].includes(Number(record.usageStatus)) &&
                                    record.usedOrderNo;
                                const preparing =
                                    used &&
                                    record.usageStatus != null &&
                                    Number(record.usageStatus) === 0;
                                return (
                                    <article
                                        className={styles.card}
                                        key={record.id || record.redeemCode}
                                    >
                                        <div className={styles.cardHeader}>
                                            <h3>{record.redeemName || `套餐第 ${index + 1} 份`}</h3>
                                            <Tag
                                                color={
                                                    canResume
                                                        ? 'orange'
                                                        : used
                                                          ? 'default'
                                                          : 'green'
                                                }
                                            >
                                                {preparing
                                                    ? '正在准备生成'
                                                    : canResume
                                                      ? '待继续生成'
                                                      : used
                                                        ? '已使用'
                                                        : '可使用'}
                                            </Tag>
                                        </div>
                                        {record.services?.length > 0 && (
                                            <div className={styles.services}>
                                                {record.services.map((service, i) => (
                                                    <Tag key={`${service}-${i}`}>{service}</Tag>
                                                ))}
                                            </div>
                                        )}
                                        <p className={styles.hint}>
                                            {preparing
                                                ? '套餐已关联到原文章。可继续确认生成进度，不会重复扣减套餐。'
                                                : canResume
                                                  ? '上次生成未能启动，可继续为原文章生成，无需再次购买。'
                                                  : used
                                                    ? '查看这份套餐对应的生成进度与文件。'
                                                    : needsServiceAssistance(record)
                                                      ? '这项服务需通过客服协助使用，套餐仍可保留或分享。'
                                                      : '点击后填写创作内容，确认生成时才会使用本份套餐。'}
                                        </p>
                                        <div className={styles.actions}>
                                            {canResume ? (
                                                <Button
                                                    type="primary"
                                                    loading={resuming === record.redeemCode}
                                                    disabled={
                                                        Boolean(resuming) &&
                                                        resuming !== record.redeemCode
                                                    }
                                                    onClick={() => handleResume(record)}
                                                >
                                                    {preparing ? '继续确认生成' : '继续生成'}
                                                </Button>
                                            ) : used ? (
                                                <Button
                                                    type="primary"
                                                    icon={<DownloadOutlined />}
                                                    onClick={() => handleViewResult(record)}
                                                >
                                                    查看生成结果 / 下载文件
                                                </Button>
                                            ) : (
                                                <>
                                                    <Button
                                                        type="primary"
                                                        onClick={() => handleUse(record)}
                                                    >
                                                        {needsServiceAssistance(record)
                                                            ? '查看使用方式'
                                                            : onSelect
                                                              ? '用于当前创作'
                                                              : '立即使用'}
                                                    </Button>
                                                    <Button
                                                        type="text"
                                                        icon={<ShareAltOutlined />}
                                                        loading={sharing === record.redeemCode}
                                                        onClick={() => handleShare(record)}
                                                    >
                                                        分享此份
                                                    </Button>
                                                    <Dropdown
                                                        trigger={['click']}
                                                        menu={{
                                                            items: [
                                                                {
                                                                    key: 'copy',
                                                                    label: '复制兑换码（旧版使用）',
                                                                    icon: <CopyOutlined />,
                                                                },
                                                            ],
                                                            onClick: () => handleCopyCode(record),
                                                        }}
                                                    >
                                                        <Button
                                                            type="text"
                                                            icon={<MoreOutlined />}
                                                            aria-label="更多套餐操作"
                                                        />
                                                    </Dropdown>
                                                </>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                        {coupons.length > 0 && (
                            <section className={styles.coupons}>
                                <h3>抵扣优惠券</h3>
                                <p className={styles.hint}>
                                    抵扣符合条件的订单费用，最终金额和使用条件在支付页确认。选择优惠券不会解锁全文。
                                </p>
                                {coupons.map((record) => {
                                    const occupied =
                                        Number(record.codeUse) === 1 ||
                                        Number(record.codeLock) === 1;
                                    return (
                                        <article
                                            key={record.id || record.redeemCode}
                                            className={styles.coupon}
                                        >
                                            <div>
                                                <strong>{record.redeemName || '优惠券'}</strong>
                                                <Tag color={occupied ? 'orange' : 'green'}>
                                                    {Number(record.codeUse) === 1
                                                        ? '已使用'
                                                        : Number(record.codeLock) === 1
                                                          ? '已用于订单'
                                                          : '可抵扣'}
                                                </Tag>
                                            </div>
                                            <div className={styles.actions}>
                                                {occupied ? (
                                                    <span className={styles.hint}>
                                                        {Number(record.codeUse) === 1
                                                            ? '这张优惠券已使用。如需帮助，请联系页面右下角客服。'
                                                            : '这张优惠券已用于订单，暂不支持在线撤回。如需更换订单，请联系页面右下角客服。'}
                                                    </span>
                                                ) : (
                                                    <>
                                                        <Button
                                                            type="primary"
                                                            onClick={() => handleUse(record)}
                                                        >
                                                            {onSelect
                                                                ? '用于当前支付'
                                                                : '选择此优惠券'}
                                                        </Button>
                                                        <Button
                                                            type="text"
                                                            icon={<CopyOutlined />}
                                                            onClick={() => handleCopyCode(record)}
                                                        >
                                                            复制抵扣码
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </article>
                                    );
                                })}
                            </section>
                        )}
                    </>
                ) : records ? (
                    <div className={styles.empty}>
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                paymentCompleted
                                    ? '正在确认套餐，请稍后刷新'
                                    : '暂未查到可展示的套餐'
                            }
                        />
                        <p>
                            刚完成支付时，套餐发放可能稍有延迟。也请确认输入的是购买套餐时的订单号或支付交易单号。
                        </p>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => queryOrder(queriedOrderNo)}
                        >
                            刷新套餐
                        </Button>
                    </div>
                ) : !error && !loading ? (
                    <div className={styles.guide}>
                        <h3>购买后这样使用</h3>
                        <ol>
                            <li>输入购买凭证，找到已购买的套餐。</li>
                            <li>点击“立即使用”，填写题目并确认创作内容。</li>
                            <li>生成后回到这里，点击“查看生成结果 / 下载文件”。</li>
                        </ol>
                        <p>购买凭证可在支付成功页，或微信 / 支付宝账单详情中找到。</p>
                    </div>
                ) : null}
            </Spin>
            {shareLink && (
                <Alert
                    className={styles.share}
                    type="info"
                    showIcon
                    message="只分享当前这一份套餐"
                    description={
                        <>
                            <p>对方打开领取链接即可开始使用。请发送给实际使用人。</p>
                            <Input
                                readOnly
                                value={shareLink}
                                aria-label="套餐领取链接"
                                onFocus={(event) => event.target.select()}
                            />
                            <Button
                                size="small"
                                onClick={async () => {
                                    try {
                                        await copyText(shareLink);
                                        message.success('领取链接已复制');
                                    } catch (err) {
                                        message.warning(err.message);
                                    }
                                }}
                            >
                                复制领取链接
                            </Button>
                        </>
                    }
                />
            )}
        </div>
    );
};

const GroupBuyingOrder = (props) => {
    const modal = useModal();
    return (
        <Modal
            title="我的套餐"
            zIndex={1900}
            open={modal.visible}
            width={800}
            footer={null}
            onCancel={() => modal.hide()}
        >
            <GroupBuyingOrderPanel
                {...props}
                visible={modal.visible}
                onClose={() => modal.hide()}
            />
        </Modal>
    );
};
export default NiceModal.create(GroupBuyingOrder);
